import {useContext, useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTrash} from "@fortawesome/free-solid-svg-icons";

// Contexts
import {useSocket} from "../../contexts/socket.js";
import {AuthContext} from "../../contexts/auth.js";
import {GlobalContext} from "../../contexts/global.js";

// Hooks
import useApi from "../../hooks/useApi.js";

// Utils
import {toTitleCase} from "../../utils/helpers.js";

// Components
import ModalDelete from "../../components/ModalDelete.js";

// Style
import {pad} from "../../style/components/variables.js";
import {Button, ButtonLoader, Text} from "../../style/components/general.js";

const FacilityArchiveDangerZone = ({
  facility,
  onRestored,
  onArchived,
  onDeleted,
  testIdPrefix = "facilityArchive"
}) => {
  const socket = useSocket();
  const {roleCanAccessResource} = useContext(AuthContext);
  const {setDeletingFacilities} = useContext(GlobalContext);
  const {api: facilitiesApi} = useApi("facilities");

  const [restoreLoading, setRestoreLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);
  const [deletePermanentLoading, setDeletePermanentLoading] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const {id, name, types = [], type: facilityType, isDeleted} = facility || {};
  const canArchive = roleCanAccessResource("facility", "archive");
  const canDelete = roleCanAccessResource("facility", "delete");

  const archiveTypes = (() => {
    if (types.length > 0) return types;
    if (facilityType) return [{id: id || facilityType, name: facilityType}];
    return [];
  })();

  const restoreFacility = () => {
    if (!id) return;
    setRestoreLoading(true);
    facilitiesApi
      .callPut(id, {restore: true})
      .then(({status}) => {
        if (status === 200 && onRestored) onRestored();
      })
      .finally(() => setRestoreLoading(false));
  };

  const archiveFacility = typeName => {
    setDeleteLoading(prev => ({...prev, [typeName]: true}));
    facilitiesApi
      .callDelete(id, {data: {name, type: typeName}})
      .then(({status}) => {
        if (status === 200 && onArchived) onArchived();
      })
      .finally(() => setDeleteLoading(prev => ({...prev, [typeName]: false})));
  };

  const archiveAll = () => {
    setDeleteAllLoading(true);
    facilitiesApi
      .callDelete(id, {data: {name, all: true}})
      .then(({status}) => {
        if (status === 200 && onArchived) onArchived();
      })
      .finally(() => setDeleteAllLoading(false));
  };

  const deleteFacilityPermanent = () => {
    setDeletePermanentLoading(true);
    facilitiesApi
      .callDelete(id, {data: {permanent: true, sid: socket?.id}})
      .then(({status}) => {
        if (status === 200) {
          setDeletingFacilities(prev => (prev ? {...prev, [id]: false} : {[id]: false}));
          if (onDeleted) onDeleted();
          else if (onArchived) onArchived();
        }
      })
      .finally(() => {
        setDeletePermanentLoading(false);
        setShowConfirmDelete(false);
      });
  };

  if (!facility || (!isDeleted && !canArchive) || (isDeleted && !canArchive && !canDelete))
    return null;

  const content = (
    <>
      {!isDeleted && canArchive && (
        <>
          <Danger>Archive facility.</Danger>
          {archiveTypes.map(({id: typeId, name: typeName}) => (
            <DangerButton
              key={typeId}
              type="button"
              title={`Archive ${typeName}`}
              onClick={() => archiveFacility(typeName)}
              loading={deleteLoading[typeName] ? 1 : 0}
              data-testid={`${testIdPrefix}.archive.${typeName}`}>
              <FontAwesomeIcon icon={faTrash} />
              Archive {typeName} {deleteLoading[typeName] ? <ButtonLoader /> : ""}
            </DangerButton>
          ))}
          {archiveTypes.length > 1 && (
            <DangerButton
              type="button"
              title="Archive All"
              onClick={archiveAll}
              loading={deleteAllLoading ? 1 : 0}
              data-testid={`${testIdPrefix}.archiveAll`}>
              <FontAwesomeIcon icon={faTrash} />
              Archive All {deleteAllLoading ? <ButtonLoader /> : ""}
            </DangerButton>
          )}
        </>
      )}

      {isDeleted && canArchive && (
        <>
          <Text>Restore archived Facility?</Text>
          <Button
            type="button"
            title="Restore"
            onClick={restoreFacility}
            loading={restoreLoading ? 1 : 0}
            data-testid={`${testIdPrefix}.restore`}>
            Restore {restoreLoading ? <ButtonLoader /> : ""}
          </Button>
        </>
      )}

      {isDeleted && canDelete && (
        <>
          <hr />
          <Danger>Danger zone.</Danger>
          <Danger>Permanently Delete Facility (This action cannot be undone).</Danger>
          <DangerButton
            type="button"
            title="Permanent Delete"
            onClick={() => setShowConfirmDelete(true)}
            data-testid={`${testIdPrefix}.delete`}>
            <FontAwesomeIcon icon={faTrash} />
            Delete
          </DangerButton>
        </>
      )}
    </>
  );

  const confirm = showConfirmDelete && (
    <ModalDelete
      visible={showConfirmDelete}
      setVisible={setShowConfirmDelete}
      confirmDelete={deleteFacilityPermanent}
      title="Permanently delete facility?"
      prompt={`This will fully remove ${name} ${toTitleCase(
        facilityType
      )} from the system and cannot be undone.`}
      loading={deletePermanentLoading}
    />
  );

  return (
    <Zone data-testid={`${testIdPrefix}.dangerZone`}>
      {content}
      {confirm}
    </Zone>
  );
};

FacilityArchiveDangerZone.propTypes = {
  facility: PropTypes.objectOf(PropTypes.any).isRequired,
  onRestored: PropTypes.func,
  onArchived: PropTypes.func,
  onDeleted: PropTypes.func,
  testIdPrefix: PropTypes.string
};

const Zone = styled.div`
  margin-top: ${pad}px;
`;

const Danger = styled(Text)`
  color: ${({theme}) => theme.error};
`;

const DangerButton = styled(Button)`
  background: ${({theme}) => theme.error};
  margin-bottom: ${pad / 2}px;

  svg {
    margin-right: ${pad}px;
  }
`;

export default FacilityArchiveDangerZone;
